import { Capacitor } from "@capacitor/core";
import { Haptics, ImpactStyle } from "@capacitor/haptics";
import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar, Style } from "@capacitor/status-bar";
import { rewriteApiUrl } from "./api-url.mjs";
import { installMobileServices } from "./mobile-services";

declare global {
  interface Window {
    __ZHIJI_APP__?: {
      apiBaseUrl: string;
      native: boolean;
      platform: string;
      version: string;
      servicesReady: boolean;
    };
  }
}

const APP_VERSION = "0.2.0";
const isNative = Capacitor.isNativePlatform();
const apiBaseUrl = readApiBaseUrl();

window.__ZHIJI_APP__ = {
  apiBaseUrl,
  native: isNative,
  platform: Capacitor.getPlatform(),
  version: APP_VERSION,
  servicesReady: false,
};

installApiRewrite(apiBaseUrl);
void installMobileServices({ apiBaseUrl, version: APP_VERSION })
  .then(() => {
    if (window.__ZHIJI_APP__) window.__ZHIJI_APP__.servicesReady = true;
  })
  .catch((error) => console.error("知几账户服务初始化失败", error));

if (isNative) {
  document.documentElement.dataset.native = "true";
  document.documentElement.dataset.platform = Capacitor.getPlatform();

  installHapticsFallback();
  void configureSystemUi();

  const hideLaunchScreen = () => {
    window.setTimeout(() => void SplashScreen.hide(), 180);
  };
  if (document.readyState === "complete") {
    hideLaunchScreen();
  } else {
    window.addEventListener("load", hideLaunchScreen, { once: true });
  }
}

function readApiBaseUrl(): string {
  const value = document
    .querySelector<HTMLMetaElement>('meta[name="zhiji-api-base"]')
    ?.content.trim();

  if (!value) {
    throw new Error("缺少知几 API 地址配置");
  }

  return value.replace(/\/$/, "");
}

function installApiRewrite(baseUrl: string): void {
  const nativeFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const request = rewriteRequest(input, baseUrl);
    const response = await nativeFetch(request, init);
    const url = typeof request === "string" ? request : request instanceof URL ? request.href : request.url;
    if (url.startsWith(`${baseUrl}/api/`) && response.headers.get("Content-Type")?.includes("text/html")) {
      throw new Error("服务连接异常，请稍后重试");
    }
    return response;
  };
}

function rewriteRequest(
  input: RequestInfo | URL,
  baseUrl: string,
): RequestInfo | URL {
  if (typeof input === "string") {
    return rewriteUrl(input, baseUrl);
  }

  if (input instanceof URL) {
    return new URL(rewriteUrl(input.toString(), baseUrl));
  }

  if (input instanceof Request) {
    const rewrittenUrl = rewriteUrl(input.url, baseUrl);
    return rewrittenUrl === input.url
      ? input
      : new Request(rewrittenUrl, input);
  }

  return input;
}

function rewriteUrl(rawUrl: string, baseUrl: string): string {
  return rewriteApiUrl(rawUrl, window.location.href, baseUrl);
}

function installHapticsFallback(): void {
  if (typeof navigator.vibrate === "function") {
    return;
  }

  const vibrate = (pattern: number | number[]): boolean => {
    const duration = Array.isArray(pattern) ? Math.max(...pattern, 0) : pattern;
    const style = duration >= 80 ? ImpactStyle.Medium : ImpactStyle.Light;
    void Haptics.impact({ style }).catch(() => undefined);
    return true;
  };

  try {
    Object.defineProperty(navigator, "vibrate", {
      configurable: true,
      value: vibrate,
    });
  } catch {
    // 某些 WebView 会把 navigator 属性设为只读；此时保留无震动降级。
  }
}

async function configureSystemUi(): Promise<void> {
  await Promise.allSettled([
    StatusBar.setStyle({ style: Style.Dark }),
    StatusBar.setOverlaysWebView({ overlay: true }),
    StatusBar.setBackgroundColor({ color: "#0B100E" }),
  ]);
}
